// src/components/cognition/tables/retrieval-strategies-table.tsx
import React, { useMemo } from "react";
import { CheckCircle2 } from "lucide-react";

type LegacyRow = {
  strategy: string;
  values?: Record<string, any>;
} & Record<string, any>;

type ModelCentricRow = {
  model: string;
  strategies?: string[];
  webResearch?: boolean;
  pageExtraction?: boolean;
  structuredData?: boolean;
  internalKnowledge?: boolean;

  // snake_case fallbacks (defensive)
  web_search?: boolean;
  web_page_extraction?: boolean;
  structured_product_data?: boolean;
  internal_knowledge?: boolean;
} & Record<string, any>;

type Row = LegacyRow | ModelCentricRow;

function normKey(s: string) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function pickCellValue(row: LegacyRow, modelId: string, modelLabel: (id: string) => string) {
  if (row.values && typeof row.values === "object") {
    const directCandidates = [
      modelId,
      modelId.toLowerCase(),
      modelId.toUpperCase(),
      modelLabel(modelId),
      modelLabel(modelId).toLowerCase(),
    ];

    for (const k of directCandidates) {
      const v = row.values[k];
      // IMPORTANT: allow false to pass through (so it renders as —)
      if (v !== undefined) return v;
    }

    const target = normKey(modelId);
    const targetLabel = normKey(modelLabel(modelId));
    for (const k of Object.keys(row.values)) {
      const nk = normKey(k);
      if (nk.includes(target) || nk.includes(targetLabel)) {
        const v = row.values[k];
        if (v !== undefined) return v;
      }
    }
  }

  const candidates = [
    modelId,
    modelId.toLowerCase(),
    modelId.toUpperCase(),
    modelLabel(modelId),
    modelLabel(modelId).toLowerCase(),
  ];
  for (const k of candidates) {
    if (k in row) return (row as any)[k];
  }
  return null;
}

function normalizeFusionLabel(x: string) {
  const v = String(x || "").toLowerCase();

  if (v.includes("web_search") || v === "web" || v.includes("search")) return "Web";
  if (v.includes("web_page") || v.includes("extraction") || v === "extraction") return "Extraction";
  if (v.includes("structured") || v.includes("product_data")) return "Structured";
  if (v.includes("internal")) return "Internal";

  return String(x || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function pillStyle(label: string) {
  const v = String(label || "").toLowerCase();

  if (v === "web") return { bg: "var(--signal-blue-5)", border: "var(--signal-blue-30)", text: "var(--signal-blue)" };
  if (v === "structured") return { bg: "rgba(245, 158, 11, 0.10)", border: "rgba(245, 158, 11, 0.35)", text: "rgba(245, 158,, 11, 1)" };
  if (v === "extraction") return { bg: "var(--alpha-teal-5)", border: "var(--alpha-teal-30)", text: "var(--alpha-teal)" };
  if (v === "internal") return { bg: "var(--infrared-coral-5)", border: "var(--infrared-coral-30)", text: "var(--infrared-coral)" };
  return { bg: "var(--muted)", border: "var(--border)", text: "var(--muted-foreground)" };
}

function FusionOrderPills({ order }: { order: string[] }) {
  const list = Array.isArray(order) ? order : [];
  if (!list.length) return <div style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>—</div>;

  return (
    <div className="space-y-2">
      {list.slice(0, 4).map((raw, idx) => {
        const label = normalizeFusionLabel(raw);
        const st = pillStyle(label);

        return (
          <div
            key={`${label}-${idx}`}
            className="rounded-lg border px-3 py-2"
            style={{
              backgroundColor: st.bg,
              borderColor: st.border,
              color: st.text,
              fontWeight: "var(--font-weight-semibold)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: "var(--text-base)" }}>{idx + 1}.</span>
            <span style={{ fontSize: "var(--text-base)" }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function looksModelCentric(rows: Row[]) {
  const r = rows?.[0] as any;
  return !!r && typeof r === "object" && "model" in r && !("strategy" in r);
}

export function RetrievalStrategiesTable({
  rows,
  fusionOrder,
  modelIds,
  modelLabel,
}: {
  rows: Row[];
  fusionOrder?: Array<{ model: string; order: string[] }>;
  modelIds: string[];
  modelLabel: (id: string) => string;
}) {
  const hasStrategies = Array.isArray(rows) && rows.length > 0;
  const hasFusion = Array.isArray(fusionOrder) && fusionOrder.length > 0;
  if (!hasStrategies && !hasFusion) return null;

  const orderMap = new Map<string, string[]>();
  for (const o of fusionOrder || []) {
    const key = String(o.model || "").toLowerCase();
    orderMap.set(key, Array.isArray(o.order) ? o.order : []);
  }

  const getOrder = (id: string) => orderMap.get(id.toLowerCase()) || orderMap.get(modelLabel(id).toLowerCase()) || [];

  const tableRows: LegacyRow[] = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) return [];

    if (!looksModelCentric(list)) {
      // already legacy strategy rows
      return list as LegacyRow[];
    }

    const perModel = new Map<string, any>();
    for (const r of list as any[]) {
      const key = String(r?.model || "").toLowerCase();
      if (key) perModel.set(key, r);
    }

    const defs = [
      {
        strategy: "Web research",
        keys: ["webResearch", "web_search"],
        tagHints: ["web research", "web_search", "search"],
      },
      {
        strategy: "Page extraction",
        keys: ["pageExtraction", "web_page_extraction"],
        tagHints: ["page extraction", "extraction"],
      },
      {
        strategy: "Structured data",
        keys: ["structuredData", "structured_product_data"],
        tagHints: ["structured data", "structured"],
      },
      {
        strategy: "Internal knowledge",
        keys: ["internalKnowledge", "internal_knowledge"],
        tagHints: ["internal knowledge", "internal"],
      },
    ];

    return defs.map((d) => {
      const values: Record<string, any> = {};

      for (const id of modelIds) {
        const r =
          perModel.get(String(id).toLowerCase()) ||
          perModel.get(String(modelLabel(id)).toLowerCase());

        if (!r) {
          values[id] = null;
          continue;
        }

        let v: any = undefined;
        for (const k of d.keys) {
          if (r[k] !== undefined) {
            v = r[k];
            break;
          }
        }

        // fallback: strategies[] tags
        if (v === undefined && Array.isArray(r.strategies)) {
          const tags = r.strategies.map((x: any) => String(x || "").toLowerCase());
          v = d.tagHints.some((h) => tags.some((t: string) => t.includes(h))) ? true : false;
        }

        values[id] = v;
      }

      return { strategy: d.strategy, values };
    });
  }, [rows, modelIds, modelLabel]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th
              className="px-4 py-3 text-left"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderTopLeftRadius: "var(--radius)",
              }}
            >
              Strategy
            </th>

            {modelIds.map((id) => (
              <th
                key={id}
                className="px-4 py-3 text-center"
                style={{
                  backgroundColor: "var(--alpha-teal-10)",
                  borderTop: "1px solid var(--border)",
                  borderLeft: "1px solid var(--border)",
                }}
              >
                {modelLabel(id)}
              </th>
            ))}

            <th
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderRight: "1px solid var(--border)",
                borderTopRightRadius: "var(--radius)",
              }}
            />
          </tr>
        </thead>

        <tbody>
          {tableRows.map((row, idx) => (
            <tr key={`${row.strategy}-${idx}`}>
              <td
                className="px-4 py-3"
                style={{
                  borderLeft: "1px solid var(--border)",
                  borderTop: "1px solid var(--border)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                {row.strategy}
              </td>

              {modelIds.map((id) => {
                const v = pickCellValue(row, id, modelLabel);
                return (
                  <td
                    key={id}
                    className="px-4 py-3 text-center"
                    style={{
                      borderLeft: "1px solid var(--border)",
                      borderTop: "1px solid var(--border)",
                    }}
                  >
                    {v === true ? (
                      <CheckCircle2 className="w-4 h-4 mx-auto" style={{ color: "var(--alpha-teal)" }} />
                    ) : v === false || v == null ? (
                      <div className="w-4 h-4 mx-auto">—</div>
                    ) : (
                      <span>{String(v)}</span>
                    )}
                  </td>
                );
              })}

              <td style={{ borderRight: "1px solid var(--border)", borderTop: "1px solid var(--border)" }} />
            </tr>
          ))}

          {!!modelIds.length && (
            <tr>
              <td
                className="px-4 py-3"
                style={{
                  borderLeft: "1px solid var(--border)",
                  borderTop: "1px solid var(--border)",
                  borderBottom: "1px solid var(--border)",
                  borderBottomLeftRadius: "var(--radius)",
                  backgroundColor: "var(--muted-30)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                Fusion Order
              </td>

              {modelIds.map((id) => (
                <td
                  key={id}
                  className="px-4 py-3"
                  style={{
                    borderLeft: "1px solid var(--border)",
                    borderTop: "1px solid var(--border)",
                    borderBottom: "1px solid var(--border)",
                    backgroundColor: "var(--muted-30)",
                    verticalAlign: "top",
                  }}
                >
                  <FusionOrderPills order={getOrder(id) || []} />
                </td>
              ))}

              <td
                style={{
                  borderRight: "1px solid var(--border)",
                  borderTop: "1px solid var(--border)",
                  borderBottom: "1px solid var(--border)",
                  borderBottomRightRadius: "var(--radius)",
                  backgroundColor: "var(--muted-30)",
                }}
              />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
